import React from "react";
/**
 * Icons specific to the organization review-status card. The sidebar's icon set lives in
 * components/common/AppSidebar/icons.tsx, since it's shared with the app shell.
 *
 * All icons share the design's stroke geometry: 24x24 box, no fill, `currentColor` stroke.
 */

type IconProps = { className?: string };

const base = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
} as const;

/** Document glyph at the centre of the review-status card. */
export const ReviewDocIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} strokeWidth={1.7} className={className}>
    <path d="M14 2.5H7A2 2 0 0 0 5 4.5v15a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7.5l-5-5Z" />
    <path d="M14 2.5v5h5" />
    <path d="M9 12h6M9 16h4" />
  </svg>
);

/** Small clock badge overlaid on the review-status card icon (pending). */
export const ClockBadgeIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} strokeWidth={2.4} className={className}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 8v4l2.6 1.8" />
  </svg>
);

/** Small cross badge overlaid on the review-status card icon (rejected). */
export const CrossBadgeIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} strokeWidth={2.6} className={className}>
    <path d="m7 7 10 10M17 7 7 17" />
  </svg>
);

/** Upload arrow on the "رفع الملف" call-to-action in each document row. */
export const UploadIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} strokeWidth={2} className={className}>
    <path d="M12 15.5V5M7.5 9 12 4.5 16.5 9" />
    <path d="M4.5 15v3A2.5 2.5 0 0 0 7 20.5h10a2.5 2.5 0 0 0 2.5-2.5v-3" />
  </svg>
);

/** Tick shown in the green badge once a document row has a file. */
export const CheckIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} strokeWidth={3} className={className}>
    <path d="M4 12.5 9.5 18 20 6.5" />
  </svg>
);

/** Card glyph for the bank-account activation task and its review row. */
export const BankCardIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} className={className}>
    <rect x="2.5" y="6" width="19" height="13" rx="2.5" />
    <path d="M2.5 10h19" />
  </svg>
);

/** House glyph for the national-address activation task and its review row. */
export const HomeIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} className={className}>
    <path d="m3 11 9-7 9 7v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 20Z" />
  </svg>
);

/** Clear (x) button inside an address field. */
export const ClearIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} strokeWidth={2.2} className={className}>
    <path d="m6 6 12 12M18 6 6 18" />
  </svg>
);

/** Clock in the review-card footnote about expected review duration. */
export const ClockIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} className={className}>
    <circle cx="12" cy="12" r="9.5" />
    <path d="M12 7.5v5l3 2" />
  </svg>
);

/** Building glyph at the centre of the portal-onboarding card and on its organization row. */
export const BuildingIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} className={className}>
    <path d="M4.5 21.5V5A1.5 1.5 0 0 1 6 3.5h8A1.5 1.5 0 0 1 15.5 5v16.5" />
    <path d="M15.5 9.5H18a1.5 1.5 0 0 1 1.5 1.5v10.5" />
    <path d="M2.5 21.5h19M8 7.5h1M11 7.5h1M8 11h1M11 11h1M8 14.5h1M11 14.5h1" />
    <path d="M9 21.5v-3h2v3" />
  </svg>
);

/** Small plus badge overlaid on the portal-onboarding card icon. */
export const PlusBadgeIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} strokeWidth={2.8} className={className}>
    <path d="M12 6v12M6 12h12" />
  </svg>
);

/** Key glyph for the portal-login row on the portal-onboarding card. */
export const KeyIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} className={className}>
    <circle cx="8" cy="15" r="4.5" />
    <path d="m11.2 11.8 8.3-8.3M16.5 6.5l2.5 2.5M14 9l2 2" />
  </svg>
);
